'use client';

import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import Link from 'next/link';

import { ReactComponent as SvgApple } from 'public/assets/icons/apple.svg';
import { ReactComponent as SvgFacebook } from 'public/assets/icons/facebook.svg';
import { ReactComponent as SvgGoogle } from 'public/assets/icons/google-play.svg';
import { ReactComponent as SvgInstagram } from 'public/assets/icons/instagram.svg';
import { ReactComponent as SvgTwitter } from 'public/assets/icons/twitter.svg';
import { ReactComponent as SvgYoutube } from 'public/assets/icons/youtube.svg';
import { ReactComponent as SvgMntBig } from 'public/assets/resort/big-mountain.svg';
import { ReactComponent as SvgMntGray } from 'public/assets/resort/small-mountain-gray.svg';
import { ReactComponent as SvgMntSmall } from 'public/assets/resort/small-mountain.svg';
import { ReactComponent as SvgLogo } from 'public/assets/white-logo.svg';
import { ReactComponent as SvgLogoSki } from 'public/assets/white-logo_ski.svg';
import { ReactComponent as SvgLogoMountainNewsSummer } from 'public/assets/mountain-news-summer.svg';
import { ReactComponent as SvgLogoMountainGetawaySummer } from 'public/assets/mountain-getaway-summer.svg';
import { UnitToggle } from '@/components/blocks/Footer/UnitToggle';
import { Routes } from '@/routes';
import { IS_SSR } from '@/ots-constants';
import { getPortalSocial } from '@/config/getPortalConfig';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { Container } from '@/components/ui/container';
import { getImpressum } from '@/i18n/preferences';
import { ELocale } from '@/i18n/localeEnum';
import { getCountry, getLang, getLocale } from '@/i18n/locale';
import { useNoHeader } from '@/util/useNoHeader';
import { isMainLocale } from '@/util/i18nFlags';

import { I18nKey } from '@/i18n/types';
import { ONE_TRUST_CONFIG } from '@/tracking/onetrust/constants';
import { useSettingsPrefetch } from '@/settings/SettingsContext';
import { SimpleLangSelect } from '@/components/resort/FooterLangSelect/FooterLangSelect';
import { useShouldAdjustForSummer } from '@/util/useShouldAdjustForSummer';
import { getSummerLogoHrefUrl } from '@/util/getSummerLogoHrefUrl';

const getAppleStoreUrl = () => {
  const locale = getLocale() as ELocale;
  const addLangParam = ['pl-PL', 'cs-CZ', 'sk-SK', 'nl-NL', 'es-ES', 'sv-SE', 'da-DK'].includes(locale);
  const region = locale !== ELocale.SV_SE ? getCountry().toLowerCase() : getLang().toLowerCase();
  if (addLangParam) {
    return `https://apps.apple.com/${region}/app/ski-snow-report/id${process.env.NEXT_PUBLIC_APPSTORE_ID}?l=${getLang().toLowerCase()}`;
  }
  return `https://apps.apple.com/${region}/app/ski-snow-report/id${process.env.NEXT_PUBLIC_APPSTORE_ID}`;
};

const googleStoreLocaleSuffixMap: Record<ELocale, string> = {
  [ELocale.FR_FR]: 'hl=fr',
  [ELocale.DE_DE]: 'hl=de',
  [ELocale.EN_GB]: 'hl=en_gb',
  [ELocale.IT_IT]: 'hl=it',
  [ELocale.PL_PL]: 'hl=pl',
  [ELocale.CS_CZ]: 'hl=cs',
  [ELocale.SK_SK]: 'hl=sk',
  [ELocale.EN_US]: 'feature=related_apps',
  [ELocale.NL_NL]: 'feature=related_apps',
  [ELocale.NN_NO]: 'feature=related_apps',
  [ELocale.ES_ES]: 'feature=related_apps',
  [ELocale.SV_SE]: 'feature=related_apps',
  [ELocale.DA_DK]: 'feature=related_apps',
};

const getGoogleStoreUrl = () => {
  const locale = getLocale() as ELocale;
  return `https://play.google.com/store/apps/details?id=${process.env.NEXT_PUBLIC_GOOGLEPLAY_ID}&${googleStoreLocaleSuffixMap[locale]}`;
};

export const socialLinks = {
  facebook: `https://www.facebook.com/${getPortalSocial().facebook}`,
  twitter: `https://www.twitter.com/${getPortalSocial().twitter}`,
  instagram: `https://www.instagram.com/${getPortalSocial().instagram}`,
  youtube: `https://www.youtube.com/user/${getPortalSocial().youtube}`,
  apple: getAppleStoreUrl(),
  google: getGoogleStoreUrl(),
};

type PigeonProps = {
  href: string,
  trKey: I18nKey['common'],
  fallback: string,
};

export const PigeonManageLink = () => {
  const ref = useRef<HTMLAnchorElement>(null);
  const { t } = useOTSTranslation('common');

  const updatePigeonProps = useCallback(() => {
    const props: PigeonProps = (global as any).pigeonProps;
    if (!props || !ref.current) {
      return undefined;
    }
    ref.current.innerText = t(props.trKey, {
      defaultValue: props.fallback,
    });
    ref.current.href = props.href;
    return undefined;
  }, [ref]);
  (global as any).updatePigeonProps = updatePigeonProps;
  useEffect(() => {
    if (!IS_SSR) {
      updatePigeonProps();
    }
  }, [updatePigeonProps]);

  /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
  return <a ref={ref} id="pigeon-btn" className="pigeon-settings">{/* Pigeon will insert label here */}</a>;
};

export const OneTrustManageLink = () => {
  const [isDomLoaded, setIsDomLoaded] = useState(false);
  const ref = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    if (!IS_SSR) {
      const t = setInterval(() => {
        if (ref.current && ref.current.innerText.trim()) {
          // OneTrust did own job
          clearInterval(t);
        } else if (typeof OneTrust !== 'undefined') {
          if (ref.current) {
            clearInterval(t);
            ref.current.innerHTML = OneTrust.GetDomainData().CookieSettingButtonText;
            ref.current.addEventListener('click', () => OneTrust.ToggleInfoDisplay());
          }
        }
      }, 500);

      return () => clearInterval(t);
    }
    return () => undefined;
  }, []);

  useEffect(() => {
    setIsDomLoaded(true);
  }, []);

  /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
  return <>{isDomLoaded && <a ref={ref} id="ot-sdk-btn" className="ot-sdk-show-settings">{/* OneTrust will insert label here */}</a>}</>;
};

export const SmartManagementLink = () => {
  const locale = getLocale() as ELocale;
  const config = ONE_TRUST_CONFIG[locale];
  const hasPigeon = !!config.pigeon;

  if (hasPigeon) {
    return <PigeonManageLink />;
  }
  return <OneTrustManageLink />;
};

export const Footer = ({ isOnSummerOverview }: { isOnSummerOverview?: boolean }) => {
  const prefetch = useSettingsPrefetch();
  const { t } = useOTSTranslation('common');
  const impressum = getImpressum();
  const showUSLogo = [ELocale.EN_US, ELocale.EN_GB, ELocale.CS_CZ, ELocale.SK_SK].includes(getLocale() as ELocale);
  const showMountainGetawaySummer = isOnSummerOverview && getLocale() === ELocale.EN_US;
  const showMountainNewsSummer = isOnSummerOverview
      && [ELocale.FR_FR, ELocale.DE_DE, ELocale.IT_IT, ELocale.EN_GB, ELocale.PL_PL].includes(getLocale() as ELocale);
  const shouldAdjustForSummer = useShouldAdjustForSummer();

  const { noHeader } = useNoHeader();
  if (noHeader) {
    return null;
  }

  const logoHrefUrl = getSummerLogoHrefUrl(isOnSummerOverview || false);

  const logoClass = 'h-[30px] w-auto fill-white max-lg:mb-10 max-lg:h-11';
  // The summer marks ship without intrinsic width/height (so the header
  // can scale them via CSS), so size them here to their native artwork
  // height rather than letting the browser fall back to 300x150.
  const renderLogo = () => {
    if (showMountainGetawaySummer) return <SvgLogoMountainGetawaySummer className="h-[31px] w-auto" />;
    if (showMountainNewsSummer) return <SvgLogoMountainNewsSummer className="h-[29px] w-auto" />;
    return showUSLogo
      ? <SvgLogo className={logoClass} />
      : <SvgLogoSki className={logoClass} />;
  };

  const socialLinkClass = [
    'ml-3 flex h-[43px] w-[43px] items-center justify-center rounded',
    'border border-[#333] transition-all',
    'hover:border-white hover:bg-white',
    '[&_svg]:h-4 [&_svg]:fill-white [&_svg]:transition-all',
    'hover:[&_svg]:fill-[#11ABD8]',
  ].join(' ');

  const copyrightClass = [
    'flex min-h-[50px] flex-wrap items-center justify-center',
    'bg-[#0E0E0E] text-xs text-white/60',
    'max-lg:py-2.5 max-lg:pb-[env(safe-area-inset-bottom)] max-lg:text-center',
    '[&_a]:text-white/60 [&_a]:underline',
  ].join(' ');

  return (
    <footer>
      {/* Dark band with mountains + content */}
      <div className="relative overflow-hidden bg-[#232323] text-white lg:h-[222px] max-lg:pb-11 max-md:pt-8">
        {/* Decorative mountains */}
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <SvgMntGray className="absolute bottom-[-20px] right-[170px] h-[89px] max-lg:right-0" />
          <SvgMntBig className="absolute bottom-0 right-0 h-[207px] max-lg:right-[-170px]" />
          <SvgMntSmall className="absolute bottom-0 left-[-20px] h-[89px]" />
        </div>

        <div className="relative z-10">
          <Container width="xl-only">
            <div className="flex items-start justify-between max-lg:flex-col max-lg:items-center max-lg:justify-center">
              {/* Left column */}
              <div>
                <nav className="mt-[68px] flex items-start max-lg:mt-0 max-lg:flex-col max-lg:items-center max-lg:justify-center">
                  <Link
                    scroll={false}
                    href={logoHrefUrl}
                    prefetch={prefetch}
                    aria-label={t('footer.landing')}
                  >
                    {renderLogo()}
                  </Link>
                  {shouldAdjustForSummer && (
                    <div className="md:hidden">
                      <SimpleLangSelect />
                    </div>
                  )}
                  <div className="ml-9 flex flex-col items-start justify-start max-lg:ml-0">
                    <div className="mb-1.5 flex flex-row max-lg:flex-col max-lg:items-center">
                      <a
                        href={Routes.contactOTS}
                        className="text-lg font-bold text-white max-lg:mb-1.5 max-lg:text-sm"
                      >
                        {t('nav.footer.contactOts')}
                      </a>
                      <span className="px-2 font-normal opacity-50 max-lg:hidden">/</span>
                      <Link
                        scroll={false}
                        href={Routes.advertise}
                        prefetch={prefetch}
                        className="text-lg font-bold text-white max-lg:mb-1.5 max-lg:text-sm"
                      >

                        {t('nav.footer.advertise')}

                      </Link>
                    </div>
                    <div className="mb-1.5 flex flex-row max-lg:flex-col max-lg:items-center">
                      {isMainLocale() && (
                        <a
                          href={Routes.newsletter(t('nav.global.newsletter'))}
                          className="text-[0.8125rem] font-normal text-white max-lg:mb-1.5"
                        >
                          {t('nav.footer.newsletter')}
                        </a>
                      )}
                    </div>
                  </div>
                </nav>
                <nav className="mt-11 text-xs text-[#6F6F6F] max-lg:mt-0 max-lg:flex max-lg:flex-col max-lg:items-center">
                  {impressum && (
                    <>
                      <a href={Routes.impressum} className="text-[#6F6F6F] max-lg:mb-1.5 max-lg:block max-lg:text-center">
                        {t('nav.footer.legalNotice')}
                      </a>
                      <span className="px-2 max-lg:hidden">/</span>
                    </>
                  )}
                  <a href={Routes.privacyPolicy} className="text-[#6F6F6F] max-lg:mb-1.5 max-lg:block max-lg:text-center">
                    {t('nav.footer.privacyPolicy')}
                  </a>
                  <span className="px-2 max-lg:hidden">/</span>
                  <a href={Routes.tos} className="text-[#6F6F6F] max-lg:mb-1.5 max-lg:block max-lg:text-center">
                    {t('nav.footer.TermsConditions')}
                  </a>
                  <span className="px-2 max-lg:hidden">/</span>
                  <a href={Routes.sitemap} className="text-[#6F6F6F] max-lg:mb-1.5 max-lg:block max-lg:text-center">
                    {t('common.sitemapHeader')}
                  </a>
                  <span className="px-2 max-lg:hidden">/</span>
                  <UnitToggle />
                  <SmartManagementLink />
                </nav>
              </div>

              {/* Right column */}
              <div className="flex flex-col items-end max-lg:items-center">
                <nav className="flex pt-14 max-lg:pt-6">
                  <Link
                    href={socialLinks.youtube}
                    aria-label="Youtube"
                    target="_blank"
                    rel="noreferrer"
                    className={socialLinkClass}
                  >
                    <SvgYoutube />
                  </Link>
                  <Link
                    href={socialLinks.twitter}
                    aria-label="Twitter"
                    target="_blank"
                    rel="noreferrer"
                    className={socialLinkClass}
                  >
                    <SvgTwitter />
                  </Link>
                  <Link
                    href={socialLinks.instagram}
                    aria-label="Instagram"
                    target="_blank"
                    rel="noreferrer"
                    className={socialLinkClass}
                  >
                    <SvgInstagram />
                  </Link>
                  <Link
                    href={socialLinks.facebook}
                    aria-label="Facebook"
                    target="_blank"
                    rel="noreferrer"
                    className={socialLinkClass}
                  >
                    <SvgFacebook />
                  </Link>
                </nav>
                <nav className="flex pt-[13px] [&_svg]:h-10 [&_svg]:fill-white">
                  <Link
                    href={socialLinks.google}
                    aria-label="Google Play"
                    target="_blank"
                    rel="noreferrer"
                    className="ml-[7px]"
                  >
                    <SvgGoogle />
                  </Link>
                  <Link
                    href={socialLinks.apple}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="App Store"
                    className="ml-[7px]"
                  >
                    <SvgApple />
                  </Link>
                </nav>
              </div>
            </div>
          </Container>
        </div>
      </div>

      {/* Copyright strip */}
      <div className={copyrightClass}>
        {t('nav.footer.copyrightMncAllRightsReserved', { value: new Date().getFullYear() })}
      </div>
    </footer>
  );
};
